/* global process */
// Webpacker inlines process.env.* at build time. When the var is missing from the build
// environment the reference survives into the bundle, and reading it at module scope throws
// `process is not defined` — which aborts ReactOnRails registration and blanks every page.
// try/catch keeps the inlined value when it exists and degrades to "no Mapbox" when it doesn't.
let MAPBOX_TOKEN;
try {
  MAPBOX_TOKEN = process.env.MAPBOX_ACCESS_TOKEN;
} catch {
  MAPBOX_TOKEN = undefined;
}

const geocodeCache = new Map();

const fetchCoordinates = async (cacheKey) => {
  const encoded = encodeURIComponent(cacheKey);
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&limit=1&types=place,locality,region,district,country&country=be,fr,lu`;
  const res = await fetch(url);
  // Rate limits and server errors are temporary: throw so the result isn't cached.
  if (res.status === 429 || res.status >= 500) throw new Error(`Geocoding failed (${res.status})`);
  if (!res.ok) return null;
  const data = await res.json();
  const feature = data?.features?.[0];
  if (!feature) return null;
  const [lng, lat] = feature.center;
  return { lat, lng };
};

/**
 * Forward-geocode a text query via Mapbox Geocoding API.
 * Returns { lat, lng } or null.
 *
 * The pending promise is cached, so offers sharing a city geocoded in parallel make a single
 * (paid) request. Only definitive answers stay cached; temporary failures are retried next time.
 */
const geocodeQuery = (query) => {
  if (!query || !MAPBOX_TOKEN) return Promise.resolve(null);

  const cacheKey = query.toLowerCase().trim();
  if (geocodeCache.has(cacheKey)) return geocodeCache.get(cacheKey);

  const request = fetchCoordinates(cacheKey).catch(() => {
    geocodeCache.delete(cacheKey);
    return null;
  });
  geocodeCache.set(cacheKey, request);
  return request;
};

/**
 * Search places via Mapbox Geocoding API (for autocomplete), with place names in
 * the UI language (`locale` such as "nl" or "fr-fr").
 * Returns array of { label, lat, lng }.
 */
export const searchPlaces = async (text, locale = "fr") => {
  if (!text || text.length < 2 || !MAPBOX_TOKEN) return [];
  try {
    const encoded = encodeURIComponent(text.trim());
    const language = (locale || "fr").split("-")[0];
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&limit=5&types=place,locality,address&country=be,fr,lu&language=${language}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    return (data?.features || []).map((f) => ({
      label: f.place_name,
      lat: f.center[1],
      lng: f.center[0],
    }));
  } catch {
    return [];
  }
};

/**
 * Mapbox Static Images URL for a light map framed around `pins` ([{ lat, lng }]).
 * Returns null without a token.
 */
export const staticMapUrl = ({ pins = [], width = 640, height = 800, color = "023130" }) => {
  if (!MAPBOX_TOKEN || pins.length === 0) return null;
  const overlay = pins.map((p) => `pin-s+${color}(${p.lng},${p.lat})`).join(",");
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${overlay}/auto/${width}x${height}@2x?padding=60&access_token=${MAPBOX_TOKEN}`;
};

/**
 * Geocode a candidate's address (city + zipcode + country) to get default coords.
 * Returns { lat, lng, label } or null.
 */
export const geocodeCandidateAddress = async (candidate) => {
  if (!candidate) return null;
  const parts = [];
  if (candidate.city) parts.push(candidate.city);
  else if (candidate.zipcode) parts.push(candidate.zipcode);
  if (candidate.region) parts.push(candidate.region.replace(/_/g, " "));
  if (candidate.country) parts.push(candidate.country);
  if (parts.length === 0) return null;
  const query = parts.join(", ");
  const coords = await geocodeQuery(query);
  if (!coords) return null;
  const label = candidate.city
    ? `${candidate.city}${candidate.country ? `, ${candidate.country.toUpperCase()}` : ""}`
    : query;
  return { ...coords, label };
};
